/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { type CKEditor5, type CKEditor5PremiumFeatures } from '@ckeditor/ckeditor5-metadata';
import { getEmailInlineStylesTransformations } from 'ckeditor5-premium-features';
import { prepareEditorConfig } from '../prepareEditorConfig/prepareEditorConfig';
import { getSubstitutedPlugins } from '../getSubstitutedPlugins/getSubstitutedPlugins';
import type { PresetView } from '../getPresets/getPresets';
import { getTranslationModuleNames } from '../translations/translations';
import { getCodeCss } from '../getCodeCss/getCodeCss';
import { CustomLinkProvider } from '../../components/CKEditor5Preview/CKEditor5/CustomLinkProvider';
import { CKEDITOR5_CSS_URL, CKEDITOR5_PREMIUM_FEATURES_CSS_URL } from '../../constants/cdns';
import { DEFAULT_HEX_COLORS } from '../../constants/defaultHexColors';

type EditorConfigurationOptions = Pick<
	Parameters<typeof prepareEditorConfig>[ 0 ],
	'editorPlugins' | 'editorLanguage' | 'editorType' | 'licenseKey' | 'toolbarOptions'
> & {
	preset: PresetView;
};

type PrepareEditorConfigForPreviewArguments = [
	EditorConfigurationOptions,
	Parameters<typeof prepareEditorConfig>[ 1 ]
];

export async function prepareEditorConfigForPreview(
	editorConfigOptions: PrepareEditorConfigForPreviewArguments[ 0 ],
	editorConfigContainers: PrepareEditorConfigForPreviewArguments [ 1 ]
): Promise<CKEditor5.EditorConfig> {
	const { editorLanguage, editorType, editorPlugins, licenseKey, preset, toolbarOptions } = editorConfigOptions;

	const editorConfig = prepareEditorConfig(
		{
			editorPlugins: getSubstitutedPlugins( editorPlugins ),
			editorLanguage,
			editorType,
			toolbarOptions,
			licenseKey,
			// eslint-disable-next-line max-len
			aiAuthorizationToken: 'Bearer eyJhbGciOiJFUzM4NCJ9.eyJhdWQiOlsiaHR0cHM6Ly9vcGVuYWkucHVibGljLWRlbW8ucHJveHkuYWkuY2tlLWNzLmNvbS8iXSwiZXhwIjoxNzUxMjkyMDAwLCJodHRwczovL29wZW5haS5wdWJsaWMtZGVtby5wcm94eS5haS5ja2UtY3MuY29tL3JvbGUiOiJja2VkaXRvci1wdWJsaWMtZGVtbyIsImlzcyI6Imh0dHBzOi8vcHVibGljLWRlbW8ucHJveHkuYWkuY2tlLWNzLmNvbS8iLCJqdGkiOiI2OWM4MzI4My05MTIzLTRkMzUtYTczMC00OTBiODllMjM2OTUiLCJzdWIiOiJhaS1hc3Npc3RhbnQtZGVtbyJ9.RFRp-17iCq0TiNhUsbI_sbcRawpv2xalTYTKyhzwmXCvLIS9qeTAEa3CR98IoI_F4cbZ75onJD1wPuuMEVeveDXaaWI8JozCH8nKzt2V9L4HQ4aIgcbEdMmxXnyxjVeE',
			aiApiUrl: 'https://openai.public-demo.proxy.ai.cke-cs.com/v1/chat/completions'
		},
		{
			...editorConfigContainers
		}
	);

	editorConfig.initialData = preset.editorContent;

	delete editorConfig.collaboration;
	delete editorConfig.cloudServices;

	if ( editorPlugins.includes( 'Mention' ) ) {
		const mentionFeeds = {
			feeds: [
				{
					marker: '#',
					feed: [
						'#apple', '#bears', '#brownie', '#cake', '#candy', '#canes', '#chocolate', '#cookie', '#cotton', '#cream',
						'#cupcake', '#danish', '#donut', '#dragée', '#fruitcake', '#gingerbread', '#gummi', '#ice', '#jelly-o',
						'#liquorice', '#macaroon', '#marzipan', '#oat', '#pie', '#plum', '#pudding', '#sesame', '#snaps', '#soufflé',
						'#sugar', '#sweet', '#topping', '#wafer'
					]
				},
				{
					marker: '@',
					feed: [
						'@Liam', '@Olivia', '@Noah', '@Emma', '@Oliver', '@Ava', '@Elijah', '@Charlotte', '@William', '@Sophia'
					]
				}
			]
		};

		editorConfig.mention = mentionFeeds;

		if ( editorConfig.comments?.editorConfig ) {
			editorConfig.comments.editorConfig.mention = mentionFeeds;
		}
	}

	if ( editorPlugins.includes( 'MergeFields' ) ) {
		editorConfig.mergeFields = {
			definitions: [
				{
					groupId: 'client',
					groupLabel: 'Client information',
					definitions: [
						{
							id: 'clientName',
							label: 'Client name',
							defaultValue: 'Client name'
						},
						{
							id: 'clientAddress',
							label: 'Address',
							defaultValue: 'Client address'
						}
					]
				},
				{
					groupId: 'serviceProvider',
					groupLabel: 'Service provider',
					definitions: [
						{
							id: 'providerName',
							label: 'Provider name',
							defaultValue: 'Provider name'
						},
						{
							id: 'providerAddress',
							label: 'Provider address',
							defaultValue: 'Provider address'
						}
					]
				},
				{
					groupId: 'newsletter',
					groupLabel: 'Newsletter',
					definitions: [
						{
							id: 'discountValue',
							label: 'Discount value',
							defaultValue: '20%'
						},
						{
							id: 'benefit1',
							label: 'Benefit 1',
							defaultValue: 'Boosts energy'
						},
						{
							id: 'benefit2',
							label: 'Benefit 2',
							defaultValue: 'Antioxidants'
						},
						{
							id: 'benefit3',
							label: 'Benefit 3',
							defaultValue: 'Immune support'
						}
					]
				},
				{
					groupId: 'agreementInformation',
					groupLabel: 'Agreement information',
					definitions: [
						{
							id: 'startDate',
							label: 'Start date',
							defaultValue: 'Start date'
						},
						{
							id: 'endDate',
							label: 'End date',
							defaultValue: 'End date'
						},
						{
							id: 'feePaymentTimeframe',
							label: 'Fee payment timeframe',
							defaultValue: 'x days'
						},
						{
							id: 'feePaymentExtendedTimeframe',
							label: 'Fee payment extended timeframe',
							defaultValue: 'y days'
						},
						{
							id: 'lateFeePercentageTotal',
							label: 'Late fee total percentage',
							defaultValue: 'x%'
						},
						{
							id: 'late30daysFee',
							label: '1-30 days late fee percentage',
							defaultValue: 'x%'
						},
						{
							id: 'late60daysFee',
							label: '31-60 days late fee percentage',
							defaultValue: 'x%'
						},
						{
							id: 'late90daysFee',
							label: '61-90 days late fee percentage',
							defaultValue: 'x%'
						},
						{
							id: 'lateOver90DaysFee',
							label: 'Over 90 days late fee percentage',
							defaultValue: 'x%'
						},
						{
							id: 'terminationNoticeDays',
							label: 'Termination notice days',
							defaultValue: '14'
						},
						{
							id: 'clientBreachTerminationDays',
							label: 'Client breach fix deadline (days)',
							defaultValue: '14'
						},
						{
							id: 'serviceProviderBreachTerminationDays',
							label: 'Provider breach fix deadline (days)',
							defaultValue: '14'
						}
					]
				}
			],
			dataSets: [
				{
					id: '78900',
					label: 'QuantumLeap Innovations',
					values: {
						clientName: 'QuantumLeap Innovations',
						clientAddress: '123 Silicon Valley Drive, Suite 500, San Francisco, CA 94111',

						providerName: 'Golden State Tech',
						providerAddress: '789 Tech Center Drive, Austin, TX 78758',

						startDate: new Date( 2025, 2, 22 ).toLocaleDateString(),
						endDate: new Date( 2026, 8, 22 ).toLocaleDateString(),

						feePaymentTimeframe: '14',
						feePaymentExtendedTimeframe: '21',
						lateFeePercentageTotal: '25%',

						late30daysFee: '5%',
						late60daysFee: '10%',
						late90daysFee: '20%',
						lateOver90DaysFee: '25%',

						terminationNoticeDays: '14',
						clientBreachTerminationDays: '5',
						serviceProviderBreachTerminationDays: '14',

						discountValue: '30%',
						benefit1: 'Boosts energy',
						benefit2: 'Antioxidants',
						benefit3: 'Immune support'
					}
				}
			]
		};
	}

	if ( editorPlugins.includes( 'CKBox' ) ) {
		editorConfig.ckbox = {
			tokenUrl: 'https://api.ckbox.io/token/demo',
			forceDemoLabel: true,
			allowExternalImagesEditing: [ /^data:/, 'origin', /ckbox/, /ckeditor\.com/ ]
		};
	}

	if ( editorPlugins.includes( 'Uploadcare' ) ) {
		editorConfig.uploadcare = {
			pubkey: '0132b29be08ee83d5ae0',
			allowExternalImagesEditing: [ /^data:/, 'origin', /ckeditor\.com/ ],
			uploader: {
				sourceList: [
					'local',
					'url',
					'dropbox',
					'gdrive',
					'facebook',
					'gphotos',
					'onedrive'
				] as Array<CKEditor5PremiumFeatures.UploadcareSource>
			}
		};
	}

	const translationImportPaths = getTranslationModuleNames( editorLanguage, editorPlugins );

	if ( translationImportPaths.length ) {
		const promises = translationImportPaths
			.map( importPath => {
				return import( /* @vite-ignore */ importPath );
			} );

		editorConfig.translations = ( await Promise.all( promises ) ).map( module => module.default );
	}

	if ( shouldGenerateCssUrl( editorPlugins ) ) {
		const previewStylesUrl = URL.createObjectURL(
			new Blob( [
				getCodeCss( { editorOptions: { editorPlugins, editorLanguage, editorType, toolbarOptions } } )
			] )
		);

		if ( editorPlugins.includes( 'ExportPdf' ) ) {
			editorConfig.exportPdf!.stylesheets = [
				previewStylesUrl,
				CKEDITOR5_CSS_URL,
				CKEDITOR5_PREMIUM_FEATURES_CSS_URL
			];
		}

		if ( editorPlugins.includes( 'ExportWord' ) ) {
			editorConfig.exportWord!.stylesheets = [
				previewStylesUrl,
				CKEDITOR5_CSS_URL,
				CKEDITOR5_PREMIUM_FEATURES_CSS_URL
			];
		}

		if ( editorPlugins.includes( 'ExportInlineStyles' ) ) {
			editorConfig.exportInlineStyles ??= {};
			editorConfig.exportInlineStyles.stylesheets = [
				previewStylesUrl,
				CKEDITOR5_CSS_URL,
				CKEDITOR5_PREMIUM_FEATURES_CSS_URL
			];
		}
	}

	if ( editorPlugins.includes( 'SourceEditing' ) ) {
		editorConfig.sourceEditing = {
			allowCollaborationFeatures: true
		};
	}

	if ( editorPlugins.includes( 'TrackChangesPreview' ) ) {
		editorConfig.trackChanges = {
			preview: {
				renderFunction: ( container, elements ) => {
					for ( const element of elements ) {
						container.appendChild( element );
					}

					container.classList.add( ...editorConfigContainers.editorContainer.classList );
				}
			}
		};
	}

	if ( editorPlugins.includes( 'Link' ) ) {
		editorConfig.extraPlugins ??= [];
		editorConfig.extraPlugins.push( CustomLinkProvider );
	}

	if ( editorPlugins.includes( 'EmailConfigurationHelper' ) ) {
		if ( editorPlugins.includes( 'Table' ) ) {
			const tablePropertiesOptions = {
				borderColors: DEFAULT_HEX_COLORS,
				backgroundColors: DEFAULT_HEX_COLORS
			} as const;

			editorConfig.table ??= {};
			editorConfig.table.tableProperties = tablePropertiesOptions;
			editorConfig.table.tableCellProperties = tablePropertiesOptions;
		}

		if ( editorPlugins.includes( 'FontColor' ) ) {
			const fontColorOptions = {
				colorPicker: {
					format: 'hex'
				},
				colors: DEFAULT_HEX_COLORS
			} as const;

			editorConfig.fontColor = fontColorOptions;
			editorConfig.fontBackgroundColor = fontColorOptions;
		}

		if ( editorPlugins.includes( 'ExportInlineStyles' ) ) {
			editorConfig.exportInlineStyles = {
				transformations: getEmailInlineStylesTransformations()
			};
		}
	}

	if ( editorPlugins.includes( 'Fullscreen' ) ) {
		editorConfig.fullscreen = {
			onEnterCallback: container => {
				container.classList.add( 'main-container' );
				container.classList.add( ...editorConfigContainers.editorContainer.classList );
			}
		};
	}

	return editorConfig;
}

function shouldGenerateCssUrl( editorPlugins: EditorConfigurationOptions[ 'editorPlugins' ] ): boolean {
	if ( editorPlugins.includes( 'ExportPdf' ) ) {
		return true;
	}

	if ( editorPlugins.includes( 'ExportWord' ) ) {
		return true;
	}

	if ( editorPlugins.includes( 'ExportInlineStyles' ) ) {
		return true;
	}

	return false;
}
